<template>
	<Transition name="modal-transition">
		<NiceModal @close="close">
			<section class="v2-advertiser-edit">

				<header class="v2-ae__header">
					<div class="v2-ae__header-icon">
						<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
							<circle cx="10" cy="7" r="3.5" stroke="currentColor" stroke-width="1.4"/>
							<path d="M3 18C3 14 6 11.5 10 11.5C14 11.5 17 14 17 18" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
						</svg>
					</div>
					<div>
						<h2 class="v2-ae__title">{{ isEdit ? company.name : $t('advertiser.add_advertiser') | capitalize }}</h2>
						<p v-if="isEdit" class="v2-ae__subtitle">Edit advertiser</p>
					</div>
					<button class="v2-ae__close" @click="close">
						<svg width="16" height="16" viewBox="0 0 16 16"><path d="M4 4L12 12M12 4L4 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
					</button>
				</header>

				<div class="v2-ae__form">
					<NiceInput
						v-model="company.name"
						:invalid="errors.name"
						:label="$t('advertiser.company_name_label') + '*' | capitalize"
					/>

					<NiceInput
						v-model="company.email"
						:invalid="errors.email"
						:label="$t('advertiser.company_email_label') + '*' | capitalize"
						placeholder="name@company.tld"
					/>

					<NiceInput
						v-model="company.phone"
						:invalid="errors.phone"
						:label="$t('advertiser.company_phone_label') + '*' | capitalize"
					/>

					<NiceInput
						v-model="company.contact_details"
						:invalid="errors.contact_details"
						:label="$t('advertiser.company_contact_details_label') + '*' | capitalize"
					/>

					<div
						v-if="errorGeneral"
						class="v2-ae__error"
					>{{ errorGeneral }}</div>
				</div>

				<!-- Status + Actions footer -->
				<div class="v2-ae__footer">
					<!-- Current status display -->
					<div v-if="isSSPAdminOrApprover && isEdit && company.status" class="v2-ae__status">
						<span class="v2-ae__status-label">Status:</span>
						<span
							class="v2-ae__status-badge"
							:class="{
								'v2-ae__status-badge--approved': company.status === STATUS_APPROVED,
								'v2-ae__status-badge--awaiting': company.status === STATUS_AWAITING_APPROVAL,
							}"
						>
							{{ company.status === STATUS_APPROVED ? 'Approved' : 'Awaiting Approval' }}
						</span>
					</div>

					<!-- Status action buttons (row above Save/Cancel) -->
					<div v-if="isSSPAdminOrApprover && isEdit && availableStatusActions.length" class="v2-ae__status-actions">
						<button
							v-for="action in availableStatusActions"
							:key="action.value"
							class="v2-ae__btn"
							:class="'v2-ae__btn--' + action.color"
							:disabled="isBusy"
							@click="setStatusAndSave(action.value)"
						>
							<svg v-if="action.icon === 'check'" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7L5.5 10.5L12 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
							<svg v-else-if="action.icon === 'x'" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 3L11 11M11 3L3 11" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
							<svg v-else-if="action.icon === 'clock'" width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="5.5" stroke="currentColor" stroke-width="1.5"/><path d="M7 4V7L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
							{{ action.label }}
						</button>
					</div>

					<!-- Dialog buttons -->
					<div class="v2-ae__actions">
						<button
							class="v2-ae__btn v2-ae__btn--cancel"
							:disabled="isBusy"
							@click="close"
						>{{ $t('common.cancel') | capitalize }}</button>

						<button
							class="v2-ae__btn v2-ae__btn--save"
							:disabled="isBusy || !isFormValid"
							@click="addOrEditAdvertiser"
						>
							<template v-if="!isEdit">{{ $t('common.add') | capitalize }}</template>
							<template v-else>{{ $t('common.save') | capitalize }}</template>
						</button>
					</div>
				</div>

			</section>
		</NiceModal>
	</Transition>
</template>


<script>
import { mapState, mapGetters } from 'vuex';

import NiceModal from '@/ui/nice-modal';
import NiceInput from '@/ui/nice-input';

import advertiserApi from '@/api/advertiser.js';

import {
	STATUS_AWAITING_APPROVAL,
	STATUS_APPROVED,
} from '@/constants/advertiser.js';


export default {
	name: 'V2AdvertiserEdit',

	components: {
		NiceModal,
		NiceInput,
	},

	props: {
		advertiserId: {
			required: false,
		},
	},

	data() {
		return {
			STATUS_AWAITING_APPROVAL,
			STATUS_APPROVED,

			advertiser: null,
			company: {
				name: '',
				email: '',
				phone: '',
				contact_details: '',
				site: '',
			},
			requiredFields: ['name', 'email', 'phone', 'contact_details'],
			errors: {
				name: null,
				email: null,
				phone: null,
				contact_details: null,
				site: null,
			},
			errorGeneral: null,
			business: [],
		};
	},

	async mounted() {
		if (this.advertiserId) {
			await this.getAdvertiserAndUpdateForm();
		}
	},

	computed: {
		...mapState('app', { appSide: 'side' }),
		...mapGetters('app', ['isSSPAdminOrApprover', 'isDSP']),

		isEdit() {
			return !!this.advertiserId;
		},

		isBusy() {
			return !!this.business.length;
		},

		isFormValid() {
			for (let f of this.requiredFields) {
				if (!this.company[f]) return false;
			}
			return true;
		},

		/**
		 * Available status transition actions based on current status.
		 * Each action: { value, label, icon, color }
		 */
		availableStatusActions() {
			if (!this.company.status) return [];

			const actions = [];
			if (this.company.status === STATUS_AWAITING_APPROVAL) {
				actions.push({
					value: STATUS_APPROVED,
					label: 'Approve',
					icon: 'check',
					color: 'approve',
				});
			}
			if (this.company.status === STATUS_APPROVED) {
				actions.push({
					value: STATUS_AWAITING_APPROVAL,
					label: 'Revoke Approval',
					icon: 'clock',
					color: 'warn',
				});
			}
			return actions;
		},
	},

	methods: {
		close() {
			this.$emit('close-add-advertiser');
			const wizardReturnUrl = sessionStorage.getItem('v2WizardReturnUrl');
			if (wizardReturnUrl) {
				sessionStorage.removeItem('v2WizardReturnUrl');
				this.$router.push(wizardReturnUrl);
			} else {
				this.$router.back();
			}
		},

		async setStatusAndSave(status) {
			this.company.status = status;
			await this.addOrEditAdvertiser();
		},

		async addOrEditAdvertiser() {
			if (!this.isFormValid) return;
			this.resetErrors();

			try {
				if (this.isEdit) {
					await advertiserApi.patch(this.advertiserId, this.company);
				} else {
					await advertiserApi.create(this.company);
				}
				this.close();
			} catch (error) {
				if (error.response && error.response.data) {
					for (var f in error.response.data) {
						this.errors[f] = error.response.data[f][0];
					}
				} else {
					this.errorGeneral = this.$t('advertiser.failed_to_create_advertiser');
				}
			}
		},

		async getAdvertiserAndUpdateForm() {
			this.advertiser = await advertiserApi.retrieve(this.advertiserId);
			this.company = { ...JSON.parse(JSON.stringify(this.advertiser)) };
		},

		resetErrors() {
			this.errorGeneral = null;
			for (var f in this.errors) {
				this.errors[f] = null;
			}
		},
	},
};
</script>


<style lang="sass" scoped>
@import '~@/assets/styles/v2-tokens.sass'

.v2-advertiser-edit
	width: 420px
	padding: 28px 32px
	font-family: $v2-font

// ── Header ──────────────────────────
.v2-ae__header
	display: flex
	align-items: flex-start
	gap: 12px
	margin-bottom: 28px

.v2-ae__header-icon
	display: flex
	align-items: center
	justify-content: center
	width: 40px
	height: 40px
	background: rgba($v2-teal, 0.1)
	border-radius: 12px
	color: $v2-teal
	flex-shrink: 0

.v2-ae__title
	font-size: 18px
	font-weight: 700
	color: $v2-text
	margin: 0
	letter-spacing: -0.01em

.v2-ae__subtitle
	font-size: 12px
	color: $v2-text-muted
	margin: 2px 0 0

.v2-ae__close
	margin-left: auto
	width: 32px
	height: 32px
	border: none
	background: #f3f4f6
	border-radius: 8px
	cursor: pointer
	color: $v2-text-muted
	display: flex
	align-items: center
	justify-content: center
	transition: all 0.15s
	flex-shrink: 0

	&:hover
		background: #e5e7eb
		color: $v2-text

// ── Form ────────────────────────────
.v2-ae__form
	display: flex
	flex-direction: column
	gap: 20px

.v2-ae__error
	color: $v2-red
	font-size: 13px
	padding: 8px 12px
	background: rgba($v2-red, 0.06)
	border-radius: 8px

// ── Footer ──────────────────────────
.v2-ae__footer
	margin-top: 28px
	padding-top: 20px
	border-top: 1px solid $v2-border

.v2-ae__status
	display: flex
	align-items: center
	gap: 8px
	margin-bottom: 16px

.v2-ae__status-label
	font-size: 12px
	color: $v2-text-muted

.v2-ae__status-badge
	display: inline-flex
	align-items: center
	padding: 4px 12px
	border-radius: 20px
	font-size: 12px
	font-weight: 600

	&--approved
		background: rgba($v2-green, 0.1)
		color: darken($v2-green, 10%)

	&--awaiting
		background: rgba(#eab308, 0.1)
		color: #a16207

// ── Status action buttons row ────────
.v2-ae__status-actions
	display: flex
	gap: 8px
	margin-bottom: 14px

.v2-ae__actions
	display: flex
	align-items: center
	gap: 10px
	justify-content: flex-end

.v2-ae__btn
	padding: 9px 20px
	border-radius: 10px
	font-family: $v2-font
	font-size: 13px
	font-weight: 600
	cursor: pointer
	transition: all 0.15s
	border: none
	display: inline-flex
	align-items: center
	gap: 6px

	&:disabled
		opacity: 0.5
		pointer-events: none

	&--cancel
		background: transparent
		border: 1.5px solid $v2-border
		color: $v2-text-muted

		&:hover
			border-color: darken($v2-border, 15%)
			color: $v2-text

	&--save
		background: $v2-teal
		color: #fff

		&:hover
			background: darken($v2-teal, 8%)

	&--approve
		background: $v2-green
		color: #fff

		&:hover
			background: darken($v2-green, 8%)

	&--reject
		background: $v2-red
		color: #fff

		&:hover
			background: darken($v2-red, 8%)

	&--warn
		background: #eab308
		color: #fff

		&:hover
			background: darken(#eab308, 8%)

	&--preapprove
		background: $v2-blue
		color: #fff

		&:hover
			background: darken($v2-blue, 8%)

	&--pause
		background: $v2-purple
		color: #fff

		&:hover
			background: darken($v2-purple, 8%)
</style>
